import React, { useMemo } from 'react';
import {
  CustomAttribute,
  QuestionnaireQuestion,
} from '@core/generated/gql-types';

type QuestionPrioritySource = Pick<
  QuestionnaireQuestion,
  'questionTitle' | 'priority'
>;

type QuestionnaireResponsesProps = {
  customAttributes?: Array<CustomAttribute | null> | null;
  questions?: Array<QuestionPrioritySource | null> | null;
  showTitle?: boolean;
};

const normaliseQuestionRef = (ref: string): string => {
  if (ref.startsWith('questionnaire_')) {
    return ref.replace('questionnaire_', '').replace(/_/g, ' ');
  }
  return ref;
};

/**
 * Portal display of questionnaire answers from job customAttributes.
 * Ordered by question priority when questions are provided (client-app parity).
 */
const QuestionnaireResponses = ({
  customAttributes,
  questions,
  showTitle = true,
}: QuestionnaireResponsesProps) => {
  const questionnaireResponses = useMemo(() => {
    if (!customAttributes?.length) return [];

    const priorityByTitle = new Map<string, number>();
    for (const question of questions || []) {
      if (!question?.questionTitle) continue;
      priorityByTitle.set(
        question.questionTitle,
        question.priority ?? Number.MAX_SAFE_INTEGER,
      );
    }

    const responses = customAttributes
      .filter((attr): attr is CustomAttribute => {
        return Boolean(
          attr?.ref && attr.value && attr.ref !== 'BLACKLISTED_USERS',
        );
      })
      .map((attr, index) => {
        const question = normaliseQuestionRef(attr.ref);
        const priority =
          priorityByTitle.get(attr.ref) ??
          priorityByTitle.get(question) ??
          Number.MAX_SAFE_INTEGER;
        return {
          question,
          answer: attr.value,
          priority,
          originalIndex: index,
        };
      });

    if (priorityByTitle.size === 0) {
      return responses;
    }

    return [...responses].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.originalIndex - b.originalIndex;
    });
  }, [customAttributes, questions]);

  if (questionnaireResponses.length === 0) {
    return null;
  }

  return (
    <div>
      {showTitle && (
        <p className="text-sm font-medium text-gray-500">
          Important Information
        </p>
      )}
      <div className={`${showTitle ? 'mt-1' : ''} space-y-3`}>
        {questionnaireResponses.map((item, index) => (
          <div key={`${item.question}-${index}`}>
            <p className="text-sm font-medium text-gray-700">{item.question}</p>
            <p className="text-sm whitespace-pre-line text-gray-900">
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionnaireResponses;
